// API client for SYJ backend
export const API_BASE = "https://syj-api-production.up.railway.app/api/v1";

// 通用 fetch 封装(带可选 token + body)
async function request(path, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  if (opts.body && typeof opts.body !== "string") {
    headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(opts.body);
  }
  const token = (() => { try { return localStorage.getItem("syj_token"); } catch { return null; } })();
  if (token) headers.Authorization = `Bearer ${token}`;
  const method = (opts.method || "GET").toUpperCase();
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  } catch (netErr) {
    // 网络层失败 — 不上报,因为多半是用户网络问题(Sentry 已 ignoreErrors 过滤)
    throw netErr;
  }
  let json;
  try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  if (!res.ok || !json.ok) {
    const err = new Error(json.error || `HTTP ${res.status}`);
    err.code = json.code;
    err.status = res.status;
    // 只把 5xx 当真正的服务端 bug 上报,4xx 是业务报错
    if (res.status >= 500) {
      try {
        // 动态 import 避免循环依赖
        import("./sentry").then(s => s.captureException(err, {
          tags: { api: true, status: res.status },
          extra: { path, method },
        })).catch(()=>{});
      } catch {}
    }
    throw err;
  }
  return json.data;
}

// 后端字段 → 前端 schema 映射
export function mapProduct(raw) {
  const labels = (raw.labels || []).map(l => String(l).toLowerCase());
  const tag = labels.includes("new") ? "NEW"
            : labels.includes("hot") ? "HOT"
            : null;
  // 是否特价商品(不计入满 $100 包邮的门槛)
  // 后端在商品数据顶层返回 is_special: true/false
  const isSpecial = !!raw.is_special;
  return {
    id: raw.id,
    name: raw.name || "",
    price: parseFloat(raw.price) || 0,
    origPrice: raw.orig_price ? parseFloat(raw.orig_price) : null,
    description: raw.description || "",
    imgUrl: (raw.images && raw.images[0]) || null,
    images: raw.images || [],
    cat: raw.category_id,
    sub: raw.category_name || "",
    tag,
    sold: 0, // backend 暂未提供
    totalStock: typeof raw.total_stock === "number" ? raw.total_stock : null, // null = 后端未提供库存数据(不灰)
    variants: (raw.variants || []).map(v => ({ ...v, stock: typeof v.stock === "number" ? v.stock : null })),
    attrs: raw.attrs || {},
    sku: raw.sku,
    barcode: raw.barcode,
    active: raw.active,
    labels,
    isSpecial,
    img: pickGradientKey(raw.category_name), // fallback 渐变占位用
    createdAt: raw.created_at || raw.createdAt || null,
  };
}

// 配送 / 定价相关常量 — 以下都是 fallback 默认值,APP 启动时调
// getAppConfig() 拉到后端 /config 后会被更新成 Railway 上配置的值。
//
// 改业务配置(运费、配送范围等)只在 Railway 设环境变量,APP 不需要重新打包。
// ⚠ 用 export let 是 ES module live binding — 这里的值更新后,所有
//   import 这个变量的模块都能拿到最新值,无需手动通知。
export let FREE_SHIP_THRESHOLD = 100; // 非特价商品满 $X 包邮
export let SHIP_CA = 8;               // 加州运费
export let SHIP_OTHER = 14;           // 外州运费
// 支持送货上门的邮编(Monterey Park / Rosemead / San Gabriel)
// 注意:用 .splice 原地 mutation,保持 array 引用一致 → import 这个数组的代码
// 不会因为重新赋值变 stale
export const DELIVERY_ZIPS = [
  "91754","91755","91756", // Monterey Park
  "91770","91771","91772", // Rosemead
  "91775","91776","91777","91778", // San Gabriel
];

// 支持送货上门的城市名(zip 不在白名单时的兜底)— 全小写
const DELIVERY_CITIES = [
  "monterey park",
  "rosemead",
  "san gabriel",
];

// 门店地址 / 营业时间 / 客服电话 — 同样从 /config 拉,有合理默认
export let PICKUP_ADDRESS = "199 W Garvey Ave, Ste 101, Monterey Park, CA 91754";
export let PICKUP_HOURS = "每日 9 AM – 9 PM";
export let CONTACT_PHONE = "+12136695678";       // tel: 链接用
export let CONTACT_PHONE_DISPLAY = "213-669-5678"; // 屏幕显示用
export let CONTACT_HOURS = "每日 9 AM – 5 PM(美西时间 PT)";

// ─────────────────────────────────────────────────────────────────
// 配置就绪监听 — 让 React 组件能在 /config 拉到新值后重新 render
//
// 用法:
//   const [, force] = useReducer(x => x + 1, 0);
//   useEffect(() => onConfigReady(() => force()), []);
// ─────────────────────────────────────────────────────────────────
const _configListeners = new Set();
export function onConfigReady(callback) {
  _configListeners.add(callback);
  return () => _configListeners.delete(callback);
}
function _notifyConfigReady() {
  _configListeners.forEach(cb => { try { cb(); } catch {} });
}

// 把 /config 接口返回的设置应用到这些 module-level 变量
// 调用时机:fetchStripeConfigWithRetry 成功后(下文 stripePromise 那段)
function applyAppConfig(config) {
  if (!config) return;
  // 配送邮编 — 原地 mutation 保持 array 引用稳定
  if (Array.isArray(config.delivery_zips) && config.delivery_zips.length > 0) {
    DELIVERY_ZIPS.splice(0, DELIVERY_ZIPS.length, ...config.delivery_zips.map(String));
  }
  if (Array.isArray(config.delivery_cities) && config.delivery_cities.length > 0) {
    DELIVERY_CITIES.splice(0, DELIVERY_CITIES.length,
      ...config.delivery_cities.map(c => String(c).toLowerCase()));
  }
  // 数字配置 — let 重新赋值(ES module live binding 让 import 同步看到)
  if (typeof config.free_ship_threshold === 'number' && config.free_ship_threshold >= 0) {
    FREE_SHIP_THRESHOLD = config.free_ship_threshold;
  }
  if (typeof config.ship_ca === 'number' && config.ship_ca >= 0) {
    SHIP_CA = config.ship_ca;
  }
  if (typeof config.ship_other === 'number' && config.ship_other >= 0) {
    SHIP_OTHER = config.ship_other;
  }
  // 字符串配置
  if (config.pickup_address) PICKUP_ADDRESS = String(config.pickup_address);
  if (config.pickup_hours) PICKUP_HOURS = String(config.pickup_hours);
  if (config.contact_phone) CONTACT_PHONE = String(config.contact_phone);
  if (config.contact_phone_display) CONTACT_PHONE_DISPLAY = String(config.contact_phone_display);
  if (config.contact_hours) CONTACT_HOURS = String(config.contact_hours);
  // 税率 — 原地 mutation 保持引用
  if (config.tax_by_zip && typeof config.tax_by_zip === 'object') {
    Object.keys(TAX_BY_ZIP_DYNAMIC).forEach(k => delete TAX_BY_ZIP_DYNAMIC[k]);
    Object.assign(TAX_BY_ZIP_DYNAMIC, config.tax_by_zip);
  }
  if (typeof config.tax_default === 'number' && config.tax_default >= 0) {
    TAX_DEFAULT_DYNAMIC.value = config.tax_default;
  }
  // 触发监听器(让 React 组件重新 render 拿新值)
  _notifyConfigReady();
}

// 判断地址是否可送货上门 — zip 优先;zip 不命中再查城市名(用户填了 91xxx
// 之外的本地小邮编但写了"Monterey Park"也能识别)。
//   addr 形参:{ zip, city, state, line1, ... }
export function isDeliverableAddress(addr) {
  if (!addr) return false;
  // 1. 邮编归一化 — "91776-1234" / "  91776 " 都剪到前 5 位数字
  const zip5 = String(addr.zip || "").replace(/\D/g, "").slice(0, 5);
  if (zip5 && DELIVERY_ZIPS.includes(zip5)) return true;
  // 2. 城市名兜底
  const city = String(addr.city || "").toLowerCase().trim();
  if (!city) return false;
  return DELIVERY_CITIES.some(c => city.includes(c));
}

// ─────────────────────────────────────────────────────────────────
// 销售税计算
// 默认本地查表,精确到 LA 主要邮编。配置 REACT_APP_TAXJAR_KEY 后
// 自动切换到 TaxJar 实时 API(免费 1500 次/月,在 taxjar.com 注册)
// ─────────────────────────────────────────────────────────────────
const TAXJAR_KEY = process.env.REACT_APP_TAXJAR_KEY || "";

// LA 主要服务区精确税率(默认值,可被 /config TAX_BY_ZIP 覆盖)
// 用 mutable 对象,applyAppConfig 时原地更新
const TAX_BY_ZIP_DYNAMIC = {
  // Monterey Park
  "91754":0.0950,"91755":0.0950,"91756":0.0950,
  // Rosemead
  "91770":0.0950,"91771":0.0950,"91772":0.0950,
  // San Gabriel
  "91775":0.0950,"91776":0.0950,"91777":0.0950,"91778":0.0950,
  // 其他周边
  "91101":0.1025,"91103":0.1025,"91104":0.1025,"91106":0.1025, // Pasadena
  "91801":0.1025,"91802":0.1025,"91803":0.1025,                 // Alhambra
  "90012":0.0950,"90013":0.0950,"90014":0.0950,"90015":0.0950, // DTLA
};
// 默认税率包到一个对象里,这样 applyAppConfig 改 .value 就能更新
const TAX_DEFAULT_DYNAMIC = { value: 0.0975 };
// 兼容旧引用(getTaxRate 里用)
const TAX_BY_ZIP = TAX_BY_ZIP_DYNAMIC;

// 各州默认税率(没有具体邮编数据时回退到州)
const TAX_BY_STATE = {
  CA:0.0725,TX:0.0625,NY:0.04,FL:0.06,IL:0.0625,WA:0.065,
  PA:0.06,OH:0.0575,GA:0.04,NC:0.0475,MI:0.06,VA:0.043,
  AZ:0.056,MA:0.0625,TN:0.07,IN:0.07,MD:0.06,MO:0.04225,
  WI:0.05,CO:0.029,MN:0.06875,SC:0.06,AL:0.04,LA:0.0445,
  KY:0.06,UT:0.0485,NV:0.0685,AR:0.065,KS:0.065,MS:0.07,
  NM:0.05125,IA:0.06,CT:0.0635,OK:0.045,ID:0.06,HI:0.04,
  WV:0.06,NE:0.055,RI:0.07,SD:0.045,ND:0.05,VT:0.06,
  WY:0.04,ME:0.055,DC:0.06,
  // 免税州
  AK:0,DE:0,MT:0,NH:0,OR:0,
};

// 前端税率查询 — 优先调后端 /tax/lookup(走 DB 缓存 + TaxJar + 多层兜底),
// 失败再走前端本地表。后端那条路径只调一次 TaxJar 就缓存到 DB,
// 之后所有用户查同 zip 都直接命中缓存,大幅省 TaxJar 调用配额。
export async function getTaxRate({zip,state,country="US"}={}) {
  // 1. 后端查询(主路径)
  if (zip || state) {
    try {
      const qs = new URLSearchParams();
      if (zip) qs.set('zip', zip);
      if (state) qs.set('state', state);
      const data = await request(`/tax/lookup?${qs}`);
      if (data && typeof data.rate === 'number') return data.rate;
    } catch (e) {
      console.warn("[tax] backend lookup failed, using local fallback:", e?.message);
    }
  }
  // 2. 本地兜底(后端挂了 / 网络问题)
  if (zip && TAX_BY_ZIP_DYNAMIC[zip] !== undefined) return TAX_BY_ZIP_DYNAMIC[zip];
  if (state && TAX_BY_STATE[state.toUpperCase()] !== undefined) return TAX_BY_STATE[state.toUpperCase()];
  return TAX_DEFAULT_DYNAMIC.value;
}

// 没有真实图时根据品类映射到本地渐变占位 key
function pickGradientKey(catName) {
  const map = {
    "连衣裙":"dress","T恤":"tshirt","T恤上衣":"tshirt","瑜伽":"yoga","瑜伽运动":"yoga",
    "牛仔裤":"jeans","内衣":"lingerie","内衣睡衣":"lingerie","情趣内衣":"lingerie",
    "鞋子":"shoes","护肤":"serum","彩妆":"lipstick","洗护":"cleanser",
    "套套":"condom","KY润滑液":"ky","隐藏罐":"mystery",
  };
  return map[catName] || "default";
}

// 仅保留 active 且有库存的商品(totalStock=null 视为后端没跟踪库存,保留)
function inStockFilter(p) {
  if (typeof p.totalStock !== "number") return true; // null = 不跟踪
  return p.totalStock > 0;
}

export async function getProducts(params = {}) {
  const qs = new URLSearchParams({ limit: 1000, ...params }).toString();
  const data = await request(`/products?${qs}`);
  return data
    .filter(p => p.active !== false)
    .map(mapProduct)
    .filter(inStockFilter);
}

// 后端搜索 — 带 debounce 时建议用 limit=50,商品多了也快
export async function searchProducts(keyword, limit = 50) {
  if (!keyword?.trim()) return [];
  const qs = new URLSearchParams({ search: keyword.trim(), limit }).toString();
  const data = await request(`/products?${qs}`);
  return data
    .filter(p => p.active !== false)
    .map(mapProduct)
    .filter(inStockFilter);
}

export async function getCategories() {
  return await request("/categories");
}

// ─── 订单相关 ──────────────────────────────────────────────────────────
// 创建订单(pending_payment 状态,不扣库存)
export async function createOnlineOrder(payload) {
  return await request("/orders/online", { method: "POST", body: payload });
}

// 支付成功后确认 → 扣库存 + status=paid
export async function confirmOrderPayment(orderId, payload = {}) {
  return await request(`/orders/${orderId}/confirm-payment`, {
    method: "POST", body: payload,
  });
}

// 取消未付款订单
export async function cancelOrder(orderId) {
  return await request(`/orders/${orderId}/cancel`, { method: "POST", body: {} });
}

// 自取订单 — 顾客确认已到店取货,后端把订单标记为 completed
export async function confirmPickup(orderId) {
  return await request(`/orders/${orderId}/confirm-pickup`, { method: "POST", body: {} });
}

// 取单笔订单详情(含 items)— 用于「再次购买」从订单恢复商品
export async function getOrderById(orderId) {
  return await request(`/orders/${orderId}`);
}

// 我的订单 — 登录后用 user_id;游客用 phone 查
export async function getMyOrders({ phone, status } = {}) {
  const qs = new URLSearchParams();
  if (phone) qs.set("phone", phone);
  if (status) qs.set("status", status);
  return await request(`/orders/my${qs.toString() ? "?" + qs : ""}`);
}

// ─── 订单详情 / 物流追踪 ──────────────────────────────────────────
export async function getOrderTracking(orderId) {
  return await request(`/orders/${orderId}/tracking`);
}

// ─── Google Maps(后端代理)──────────────────────────────────────
export async function mapsAutocomplete(input) {
  if (!input?.trim()) return [];
  const qs = new URLSearchParams({ input, country: "us" });
  return await request(`/maps/autocomplete?${qs}`);
}

export async function mapsPlaceDetails(placeId) {
  return await request(`/maps/place-details?place_id=${encodeURIComponent(placeId)}`);
}

export async function mapsReverseGeocode(lat, lng) {
  // request() 已经返回 json.data,直接拿到 {line1, city, state, zip, ...}
  return await request(`/maps/reverse-geocode?lat=${lat}&lng=${lng}`);
}

// ─── APP 启动配置 ─────────────────────────────────────────────────────────
// 拿动态后端配置(Stripe pk + 配送 + 运费 + 税率 + 客服 等)— 这样改业务配置
// 只改 Railway env 不需重新上架。getAppConfig 每次成功后自动调 applyAppConfig
// 把所有配置同步到 module-level 变量 + 触发 onConfigReady 监听者(让 React
// 组件重新 render 拿新值)。
export async function getAppConfig() {
  const data = await request(`/config`);
  try { applyAppConfig(data); } catch (e) { console.warn("[config] apply failed:", e?.message); }
  return data;
}

// 暴露 dynamic getter 让消费者读最新税率
export function getCurrentTaxByZip() { return TAX_BY_ZIP_DYNAMIC; }
export function getCurrentTaxDefault() { return TAX_DEFAULT_DYNAMIC.value; }

// ─── 在线客服(Twilio Conversations)──────────────────────────────
// guestId 由前端 ChatPanel 在 localStorage 里维护一个长期 UUID,
// 未登录时也能发消息;登录用户 guestId 仍可传(后端忽略,优先使用 customer JWT)
export async function chatSend(message, guestId) {
  const body = { message };
  if (guestId) body.guestId = guestId;
  return await request("/customer-chat/send", { method: "POST", body });
}

export async function chatList(guestId) {
  const qs = guestId ? `?guestId=${encodeURIComponent(guestId)}` : "";
  return await request(`/customer-chat/messages${qs}`);
}

// ─── 顾客认证(手机号 OTP)─────────────────────────────────────────
export async function sendOtp(phone) {
  return await request("/customer-auth/send-otp", {
    method: "POST", body: { phone },
  });
}

export async function verifyOtp(phone, code, referralCode) {
  // 这个接口的响应格式是 {ok,token,user}(token 在顶层不在 data),
  // 所以不能走 request() 的 json.data 解构
  const body = { phone, code };
  if (referralCode && /^[A-Z0-9]{4,10}$/i.test(referralCode.trim())) {
    body.referral_code = referralCode.trim().toUpperCase();
  }
  const res = await fetch(`${API_BASE}/customer-auth/verify-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let json;
  try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  if (!res.ok || !json.ok) throw new Error(json.error || "登录失败");
  return { token: json.token, user: json.user };
}

export async function getMe() {
  return await request("/customer-auth/me");
}

// Sign in with Apple — 拿 Apple identityToken 换 SYJ JWT
export async function signInWithApple({ identityToken, fullName, email }) {
  const res = await fetch(`${API_BASE}/customer-auth/sign-in-with-apple`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identityToken, fullName, email }),
  });
  let json; try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  if (!res.ok || !json.ok) throw new Error(json.error || "Apple 登录失败");
  return { token: json.token, user: json.user };
}

// 反馈提交
export async function submitFeedback(payload) {
  return await request("/feedback", { method: "POST", body: payload });
}

// 更新顾客资料。当传入的 phone 命中其他账号(典型:Apple 用户绑定旧的手机号 OTP 账号)
// 后端会自动合并并返回新 JWT。这里手写 fetch 而不走 request(),因为需要拿到顶层
// 的 token / merged 字段(request() 只返回 data)
export async function updateMe(payload) {
  const token = (() => { try { return localStorage.getItem("syj_token"); } catch { return null; } })();
  const res = await fetch(`${API_BASE}/customer-auth/me`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  let json;
  try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  if (!res.ok || !json.ok) {
    const err = new Error(json.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  // 合并模式 → 返回 {data, merged:true, token, message}
  // 普通模式 → 返回 {data}
  return {
    data: json.data,
    merged: !!json.merged,
    newToken: json.token || null,
    message: json.message || null,
  };
}

export async function deleteMe() {
  return await request("/customer-auth/me", { method: "DELETE" });
}

// ─── 顾客云端数据(地址 / 购物车 / 收藏)──────────────────────────────
// 全部需要登录(customer JWT 自动通过 request() 注入)
//
// 字段映射: 后端用 line1/apt/zip/is_default,前端 AddressPanel 现在用 street/isDefault,
// 这里做一层适配,前端代码继续用旧字段名,API 层翻译成后端 schema。
function addrToServer(a) {
  if (!a) return a;
  return {
    name: a.name,
    phone: a.phone,
    line1: a.street ?? a.line1,
    apt: a.apt || "",
    city: a.city,
    state: a.state,
    zip: a.zip,
    country: a.country || "US",
    is_default: a.isDefault ?? a.is_default,
  };
}
function addrFromServer(a) {
  if (!a) return a;
  // 同时输出 street(旧 AddressPanel 用)和 line1(CheckoutPage / addrLabel / 后端 payload 用)
  // 避免某些地方读 a.line1 时 undefined
  const line1 = a.line1 || a.street || "";
  return {
    id: a.id,
    name: a.name,
    phone: a.phone,
    street: line1,
    line1: line1,
    apt: a.apt || "",
    city: a.city,
    state: a.state,
    zip: a.zip,
    country: a.country || "US",
    isDefault: !!a.is_default,
  };
}

export async function getAddresses() {
  const list = await request("/customer-data/addresses");
  return (list || []).map(addrFromServer);
}

export async function createAddress(addr) {
  const data = await request("/customer-data/addresses", {
    method: "POST", body: addrToServer(addr),
  });
  return addrFromServer(data);
}

export async function updateAddress(id, patch) {
  const data = await request(`/customer-data/addresses/${id}`, {
    method: "PUT", body: addrToServer(patch),
  });
  return addrFromServer(data);
}

export async function deleteAddress(id) {
  return await request(`/customer-data/addresses/${id}`, { method: "DELETE" });
}

export async function setDefaultAddress(id) {
  return await request(`/customer-data/addresses/${id}/default`, {
    method: "POST", body: {},
  });
}

// 购物车 / 收藏 — 全量替换式同步,localStorage 是离线缓存
export async function getCloudCart() {
  return await request("/customer-data/cart");
}
export async function putCloudCart(items) {
  return await request("/customer-data/cart", {
    method: "PUT", body: { items },
  });
}
export async function getCloudWishlist() {
  return await request("/customer-data/wishlist");
}
export async function putCloudWishlist(productIds) {
  return await request("/customer-data/wishlist", {
    method: "PUT", body: { product_ids: productIds },
  });
}

// ─── 积分系统 ─────────────────────────────────────────────────────
export async function getPointProducts() {
  return await request("/points/products");
}
export async function getPointBalance() {
  return await request("/points/balance");
}
export async function getPointHistory(limit = 50) {
  return await request(`/points/history?limit=${limit}`);
}
// payload: { point_product_id, delivery_method, delivery_address?, customer_phone?, customer_name? }
export async function redeemPointProduct(payload) {
  return await request("/points/redeem", { method: "POST", body: payload });
}

// ─── Stripe ──────────────────────────────────────────────────────
// 创建 PaymentIntent — 返回 { clientSecret, paymentIntentId, amount, currency }
export async function createStripePaymentIntent(orderId) {
  return await request("/stripe/create-payment-intent", {
    method: "POST", body: { order_id: orderId },
  });
}

// 已保存的支付方式
export async function getSavedPaymentMethods() {
  return await request("/stripe/payment-methods");
}

// 创建 SetupIntent — 用于「单独保存一张卡」流程(无订单)
export async function createSetupIntent() {
  return await request("/stripe/payment-methods/setup-intent", {
    method: "POST", body: {},
  });
}

// 游客创建 SetupIntent — 不挂 customer,只用于前端 PaymentElement 收集卡
//   返回的 pm.id 用于一次性下单,不保存
export async function createGuestSetupIntent() {
  return await request("/stripe/payment-methods/guest-setup-intent", {
    method: "POST", body: {},
  });
}

export async function setDefaultPaymentMethod(pmId) {
  return await request(`/stripe/payment-methods/${pmId}/default`, {
    method: "POST", body: {},
  });
}

export async function deletePaymentMethod(pmId) {
  return await request(`/stripe/payment-methods/${pmId}`, { method: "DELETE" });
}

// 一键用已保存的卡支付(off-session)— 成功直接返回,失败可能需要 3DS
// 不能用 request() 因为 ok:false 也是合法响应(需要 3DS 时)
export async function chargeSavedCard(orderId, paymentMethodId) {
  const headers = { "Content-Type": "application/json" };
  const token = (() => { try { return localStorage.getItem("syj_token"); } catch { return null; } })();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/stripe/charge-saved`, {
    method: "POST", headers,
    body: JSON.stringify({ order_id: orderId, payment_method_id: paymentMethodId }),
  });
  let json;
  try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  return json;
}

// ─── 图片上传(R2)─────────────────────────────────────────────────
// 顾客端用于:退换货凭证上传、反馈截图等
// 返回: { url, key }
export async function uploadImage(file) {
  if (!file) throw new Error("请选择文件");
  if (file.size > 5 * 1024 * 1024) throw new Error("图片不能超过 5MB");
  const fd = new FormData();
  fd.append("image", file);
  const token = (() => { try { return localStorage.getItem("syj_token"); } catch { return null; } })();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/upload/image`, {
    method: "POST",
    headers,
    body: fd,
  });
  let json;
  try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  if (!res.ok || !json.ok) {
    throw new Error(json.error || "上传失败");
  }
  return { url: json.url, key: json.key };
}

// ─── 退换货自助 ───────────────────────────────────────────────────
export async function getMyRefunds() {
  return await request("/refunds/my");
}

// ─── 已购商品列表(用于"再次购买"面板)───────────────────────────────
// 后端返回去重的商品列表(按最近购买排序),不返回订单
export async function getPurchasedProducts() {
  return await request("/orders/purchased-products");
}

// payload: { order_id, type:'refund'|'exchange', items:[{order_item_id, qty, reason?}], reason, photos:[url] }
export async function createRefundRequest(payload) {
  return await request("/refunds", { method: "POST", body: payload });
}

export async function cancelRefundRequest(id) {
  return await request(`/refunds/${id}/cancel`, { method: "POST", body: {} });
}

// ─── Profile 一次性聚合 ────────────────────────────────────────────
export async function getProfileStats() {
  return await request("/customer-data/profile-stats");
}

// ─── 推荐有礼 ─────────────────────────────────────────────────────
export async function getMyReferral() {
  return await request("/referrals/me");
}
export async function claimReferralCode(code) {
  return await request("/referrals/claim", { method: "POST", body: { code } });
}

// ─── 个性化推荐 ───────────────────────────────────────────────────
export async function getRecommendations({ limit = 10, excludeId = null } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (excludeId) qs.set("exclude_id", excludeId);
  // 后端格式: {ok, data:[...products], source:'personalized'|'trending'|'empty'}
  // 不能直接用 request() 因为我们需要 source 字段
  const headers = {};
  const token = (() => { try { return localStorage.getItem("syj_token"); } catch { return null; } })();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/marketing/recommendations?${qs}`, { headers });
  let json; try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  if (!res.ok || !json.ok) throw new Error(json.error || "拉取失败");
  return { products: (json.data || []).map(mapProduct), source: json.source };
}

// ─── 以图搜图 ─────────────────────────────────────────────────────
// image: data URL 字符串 "data:image/jpeg;base64,..."
export async function imageSearch(image) {
  const res = await fetch(`${API_BASE}/marketing/image-search`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image }),
  });
  let json; try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  if (!res.ok || !json.ok) throw new Error(json.error || "识别失败");
  return { products: (json.data || []).map(mapProduct), tags: json.tags || [], fallback: !!json.fallback };
}

// ─── 商品详情页配送预估 ───────────────────────────────────────────
// 后端按 (zip, ship_date) 缓存,同邮编当天只查一次 EasyPost
export async function getDeliveryEstimate(zip) {
  const z = String(zip || "").replace(/\D/g, "").slice(0, 5);
  if (z.length !== 5) throw new Error("请输入有效的 5 位邮编");
  const res = await fetch(`${API_BASE}/delivery-estimate?zip=${z}`);
  let json;
  try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  if (!res.ok || !json.ok) throw new Error(json.error || "查询失败");
  return json.data;
}

// ─── Banner ──────────────────────────────────────────────────────
export async function getBanners() {
  return await request("/banners");
}

// ─── 优惠券 ──────────────────────────────────────────────────────
export async function getMyCoupons() {
  return await request("/coupons/my");
}

export async function redeemCoupon(code) {
  return await request("/coupons/redeem", {
    method: "POST", body: { code: String(code).trim().toUpperCase() },
  });
}

// 预览优惠券效果(下单前算可省多少)
// items: [{ product_id, qty, unit_price, is_special }]  (category_id 由后端反查)
// 返回 { ok, coupon, discount, eligible_subtotal } 或 { ok:false, error }
export async function previewCoupon({ code, items, subtotal }) {
  // 直接 fetch 不走 request() — 因为 ok=false 也是合法响应,不应抛 Error
  const headers = { "Content-Type": "application/json" };
  const token = (() => { try { return localStorage.getItem("syj_token"); } catch { return null; } })();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/coupons/preview`, {
    method: "POST", headers,
    body: JSON.stringify({ code, items, subtotal }),
  });
  let json;
  try { json = await res.json(); } catch { json = { ok: false, error: `HTTP ${res.status}` }; }
  return json;
}

// ─── APP 版本检查 ─────────────────────────────────────────────────
// 当前 APP 版本,发版时改这里(同时 package.json version 同步)
export const APP_VERSION = "1.0.0";

export async function getAppVersion() {
  return await request("/version");
}

// 简单语义化版本比较: a 比 b 小返回 -1,大返回 1,等返回 0
export function compareVersions(a, b) {
  const pa = String(a || "0").split(".").map(n => parseInt(n, 10) || 0);
  const pb = String(b || "0").split(".").map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] || 0, y = pb[i] || 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
}

// 本地 token 管理
export const TOKEN_KEY = "syj_token";
export const USER_KEY  = "syj_user";

export function saveAuth(token, user) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {}
  // Sentry 关联用户 — 失败也不影响登录流程
  try { import("./sentry").then(s => s.setUser(user)).catch(()=>{}); } catch {}
}

export function clearAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {}
  try { import("./sentry").then(s => s.setUser(null)).catch(()=>{}); } catch {}
}

export function loadStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
